import React, { useState } from 'react';
import { Sparkles, ArrowRight, Check, X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Post-checkout upsell card. Shown on /checkout/success right above the
 * "Continue Shopping / Download Product" action buttons.
 *
 * Pitches Galaxy.ai as the AI runtime that powers the prompts/agents/tools
 * the buyer just purchased on theailedger.ai. Tagged with a unique UTM
 * (`buyer_postcheckout`) so we can measure conversion separately from the
 * email channel (which uses `buyer_purchase`).
 *
 * Dismissible — buyers who don't want it can close the card with one click,
 * and the dismiss is remembered for the session via sessionStorage so it
 * doesn't re-appear if they navigate back.
 */
const UTM_AFFILIATE_URL =
  'https://try.galaxy.ai/theailedger?utm_source=theailedger&utm_medium=postcheckout&utm_campaign=buyer_postcheckout';

export default function PostCheckoutUpsell() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('postcheckout_upsell_dismissed') === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem('postcheckout_upsell_dismissed', '1');
    } catch {
      /* sessionStorage blocked — fine, just hide for this render */
    }
    setDismissed(true);
  };

  const claim = () => {
    window.open(UTM_AFFILIATE_URL, '_blank', 'noopener,noreferrer');
    dismiss();
  };

  return (
    <div
      className="relative mb-6 rounded-2xl bg-[#00F0FF]/5 border border-[#00F0FF]/30 p-5 md:p-6 overflow-hidden"
      data-testid="post-checkout-upsell"
    >
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 bg-[#00F0FF]/15 blur-3xl rounded-full" />

      {/* Dismiss */}
      <button
        onClick={dismiss}
        aria-label="Dismiss recommendation"
        className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-neutral-400 hover:text-white transition-colors"
        data-testid="post-checkout-upsell-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#00F0FF]">
            Pro tip
          </span>
        </div>

        <h3 className="text-xl md:text-2xl font-black text-white mb-2">
          Need an AI stack to actually USE what you just bought?
        </h3>
        <p className="text-sm md:text-base text-neutral-300 mb-4 leading-relaxed">
          Galaxy.ai bundles <strong className="text-white">5,000+ AI tools</strong> — ChatGPT,
          Claude, Midjourney, Veo&nbsp;3.1, Sora&nbsp;2, ElevenLabs, and more — into one
          subscription so you can put your purchase to work today.
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-300">
            <Check className="w-3.5 h-3.5 text-[#00FF94]" /> $15 / month, all included
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-300">
            <Check className="w-3.5 h-3.5 text-[#00FF94]" /> Cancel anytime
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-300">
            <Check className="w-3.5 h-3.5 text-[#00FF94]" /> Used by 2M+ creators
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Button
            onClick={claim}
            className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold h-11 px-6 rounded-xl"
            data-testid="post-checkout-upsell-cta"
          >
            Try Galaxy.ai
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <button
            onClick={dismiss}
            className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors px-2 self-center"
            data-testid="post-checkout-upsell-skip"
          >
            No thanks, take me to my downloads
          </button>
        </div>

        {/* FTC disclosure — clear, conspicuous, near the link */}
        <div className="mt-4 flex items-start gap-1.5 text-[11px] text-neutral-500" data-testid="post-checkout-upsell-disclosure">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>
            Affiliate link &mdash; theailedger.ai may earn a commission when you sign up,
            at no extra cost to you. We only recommend tools we&rsquo;d use ourselves.
          </span>
        </div>
      </div>
    </div>
  );
}
